import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Field from "../Field";
import { Input } from "../index";
import TypeaheadItem from "./TypeaheadItem";

export const TypeaheadContext = React.createContext({
  onChange: () => {}
});

/** Input field that suggests potential search options as the user types. */
const Typeahead = props => {
  const {
    className,
    inputProps,
    fieldProps,
    children,
    onChange,
    onClick,
    ...other
  } = props;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [cursorLock, setCursorLock] = useState(false);
  const fieldRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (isMenuOpen) {
      // When the component mounts, add the Unity JS and Mouse Down Event
      if (!document.body.classList.contains("em-js")) {
        document.body.classList.add("em-js");
      }
      document.addEventListener("mousedown", handleClickOutside);

      // When the component unmounts, remove the Unity JS and Mouse Down Event
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isMenuOpen]);

  useEffect(() => {
    if (cursor > children.length - 1) {
      setCursor(children.length - 1);
    }
  }, [children, setCursor]);

  function handleClickOutside(event) {
    if (
      !fieldRef.current.contains(event.target) &&
      !menuRef.current.contains(event.target)
    ) {
      setIsMenuOpen(false);
    }
  }

  function handleTypeaheadItemClick(e, value) {
    e.stopPropagation();
    setIsMenuOpen(false);
    setCursorLock(false);

    if (onChange) {
      onChange(e, value);
    }
  }

  function handleFieldClick(e) {
    if (onClick) {
      onClick(e);
    }
    setIsMenuOpen(true);
  }

  function handleKeyDown(e) {
    e.stopPropagation();
    if (e.keyCode === 9 || e.keyCode === 13) {
      if (cursor >= 0 && cursor < children.length) {
        handleTypeaheadItemClick(e, children[cursor].props.value);
      }
    } else if (e.keyCode === 8 || e.keyCode === 46) {
      if (!isMenuOpen) {
        setIsMenuOpen(true);
      }
    } else if (!cursorLock) {
      if (e.keyCode === 38 && cursor > 0) {
        setCursor(prevCursor => prevCursor - 1);
      } else if (e.keyCode === 40 && cursor < children.length - 1) {
        setCursor(prevCursor => prevCursor + 1);
      }
    }
  }

  function handleMouseEnter(next, index) {
    return e => {
      setCursor(index);
      setCursorLock(true);

      if (next) {
        next(e);
      }
    };
  }

  function handleMouseLeave(next) {
    return e => {
      setCursor(-1);
      setCursorLock(false);

      if (next) {
        next(e);
      }
    };
  }

  const parsedChildren = React.Children.map(children, (child, index) => {
    if (cursor === index) {
      return React.cloneElement(child, {
        className: `${child.props.className} em-c-typeahead-list__item-active`,
        onMouseEnter: handleMouseEnter(child.props.onMouseEnter, index),
        onMouseLeave: handleMouseLeave(child.props.onMouseLeave)
      });
    }
    return React.cloneElement(child, {
      onMouseEnter: handleMouseEnter(child.props.onMouseEnter, index),
      onMouseLeave: handleMouseLeave(child.props.onMouseLeave)
    });
  });

  return (
    <TypeaheadContext.Provider value={{ onChange: handleTypeaheadItemClick }}>
      <Field
        className={clsx("em-js-typeahead", className)}
        onClick={handleFieldClick}
        innerRef={fieldRef}
        {...other}
        {...fieldProps}
      >
        {fieldProps && fieldProps.label && (
          <Field.Label>{fieldProps.label}</Field.Label>
        )}
        <Field.Body>
          <Input onKeyDown={handleKeyDown} {...inputProps} />
          <div
            ref={menuRef}
            className={clsx(
              "em-c-field__menu",
              { "em-is-active": isMenuOpen },
              "em-js-typeahead-menu"
            )}
          >
            <ul className="em-c-typeahead-list">{parsedChildren}</ul>
          </div>
        </Field.Body>
        {fieldProps && fieldProps.note && (
          <Field.Note>{fieldProps.note}</Field.Note>
        )}
      </Field>
    </TypeaheadContext.Provider>
  );
};

Typeahead.Item = TypeaheadItem;

Typeahead.propTypes = {
  /** Additional css classes to append to the outer element */
  className: PropTypes.string,
  /** Props sent to the Input component */
  inputProps: PropTypes.object,
  /** Props sent to the Field component */
  fieldProps: PropTypes.object,
  /** Callback whenever an item is clicekd */
  onChange: PropTypes.func,
  /** Callback whenever the input is clicked */
  onClick: PropTypes.func,
  /** children of component */
  children: PropTypes.node
};

Typeahead.defaultProps = {
  className: "",
  children: null
};

export default Typeahead;
